import { useState } from 'react';
import { add, getActiveLocations, ImportTypeLocation, ImportTypeLocationSchema } from '@/api/admin/location';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddImportTypeLocation({ importTypeId }: { importTypeId: number }) {
	const databaseInstances: Array<{ id: number; name: string }> = [
		{ id: 0, name: 'All' },
		{ id: 1, name: 'PIPS' },
		{ id: 2, name: 'AIM ECC' },
		{ id: 3, name: 'AIM RLA' },
		{ id: 4, name: 'AIM South & Western' },
		{ id: 5, name: 'AIM Texas Security General' },
	];

	const { data: activeLocations, isLoading: isLoadingActiveLocations } = useQuery({
		queryKey: [`active-locations`],
		queryFn: () => getActiveLocations(),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
	});

	const [open, setOpen] = useState(false);
	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newImportTypeLocation: ImportTypeLocation) => add(newImportTypeLocation),
		onSuccess: () => {
			toast.success(`Success! Location has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`locations-${importTypeId}`],
			});
			reset();
			setIsOtherSelected(false);
		},
		onError: () => {
			toast.error(`Error! Location could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeLocation>({
		resolver: zodResolver(ImportTypeLocationSchema),
		mode: 'all',
		defaultValues: {
			importTypeId: importTypeId
		},
	});

	const { reset } = form;
	const onSubmit: SubmitHandler<ImportTypeLocation> = (values: ImportTypeLocation) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Location
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new location</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Location</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="compLocId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={activeLocations}
											valueKey='compLocId'
											displayKey='brandName'
											isLoading={isLoadingActiveLocations}
											label="Company Location"
											placeholder="Select company location..."
											searchPlaceholder="Search company locations..."
											required={true}
											emptyMessage="No company location found."
											loadingMessage="Loading company locations..."
										/>
									)}
								/>										
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
